import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Mail, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_2999b2bb-a766-4d2f-9d69-24fdb875566a/artifacts/5zlw12hm_fa-logo.png";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  if (user && typeof user === "object") {
    return <Navigate to="/dashboard" replace />;
  }

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      triggerShake();
      return;
    }

    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.";
      setError(msg);
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] p-4 sm:p-8"
      data-testid="login-page"
    >
      {/* Subtle accent backdrop */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F50E02]/5 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#F50E02]/5 blur-3xl" />
      </div>

      <div
        className={`relative w-full max-w-md bg-white border border-black/5 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-8 sm:p-10 animate-in fade-in slide-in-from-bottom-2 duration-500 ${
          shake ? "shake" : ""
        }`}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-black rounded-full p-2 mb-4" data-testid="brand-logo-wrapper">
            <img
              src={LOGO_URL}
              alt="Sree LPG Fuel Access"
              className="h-16 w-16 object-contain"
              data-testid="brand-logo"
            />
          </div>
          <h1
            className="font-display text-2xl sm:text-3xl tracking-tight font-semibold text-center text-[#0A0A0A]"
            data-testid="brand-title"
          >
            Sree LPG <span className="text-[#F50E02]">Fuel Access</span>
          </h1>
          <p
            className="text-sm text-[#525252] mt-2 text-center"
            data-testid="brand-subtitle"
          >
            Sign in to your superadmin panel
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate data-testid="login-form">
          <div className="mb-5">
            <Label
              htmlFor="email"
              className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block"
            >
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@sreelpg.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-[#FAFAFA] border-black/10 text-[#0A0A0A] placeholder:text-[#A3A3A3] focus-visible:ring-1 focus-visible:ring-[#F50E02] focus-visible:border-[#F50E02] rounded-md pl-10 pr-4"
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="mb-2">
            <div className="flex items-center justify-between mb-1.5">
              <Label
                htmlFor="password"
                className="text-xs font-semibold uppercase tracking-wider text-[#525252]"
              >
                Password
              </Label>
              <button
                type="button"
                className="text-xs text-[#525252] hover:text-[#F50E02] transition-colors"
                onClick={(e) => e.preventDefault()}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 bg-[#FAFAFA] border-black/10 text-[#0A0A0A] placeholder:text-[#A3A3A3] focus-visible:ring-1 focus-visible:ring-[#F50E02] focus-visible:border-[#F50E02] rounded-md pl-10 pr-11"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#0A0A0A] transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Error state */}
          <div className="min-h-[24px] mt-3">
            {error ? (
              <p
                className="text-sm text-[#F50E02] font-medium"
                data-testid="login-error"
              >
                {error}
              </p>
            ) : null}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="bg-[#F50E02] hover:bg-[#D80C02] text-white rounded-md h-12 w-full font-medium transition-all active:scale-[0.98] mt-3 flex items-center justify-center disabled:opacity-70"
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        <p
          className="text-xs text-[#A3A3A3] text-center mt-8"
          data-testid="login-footer"
        >
          Authorized access only. Accounts are managed by the administrator.
        </p>
      </div>

      <p className="text-xs text-[#A3A3A3] mt-6 relative" data-testid="copyright">
        © {new Date().getFullYear()} Sree LPG · Fuel Access Panel
      </p>
    </div>
  );
}
